import { FunctionComponent, useMemo, useState, useCallback, useRef, useEffect } from "react";

import { Box, Stack } from "@mui/material";
import {
  StripeRecurringInterval,
  getCurrencyFromCountry,
  convertTimestampToDateNumber,
  Undefinable,
  Utils,
  Emptyable,
  ProductDonationRecurrenceInterval,
  PaymentMethod,
  AmplitudeFlagKey,
  FormCategory,
  FormType,
  Locales,
  TicketingFormCategory,
  FeatureFlagName,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { v4 } from "uuid";

import { NotificationBanners } from "../components/NotificationBanners/NotificationBanners";
import { useComputeTip } from "../components/PaymentProcessor/hooks/useComputeTip";
import { AmplitudeEvents } from "../constants/amplitude";
import { IS_PREVIEW_QUERY_PARAM } from "../constants/url";
import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { useLocaleContext } from "../contexts/LocaleContext";
import { GetCurrentUserFrontendObject } from "../contexts/types";
import { setUserProperty } from "../helpers/amplitude";
import { getDonationFormFeatureFlagTreatment } from "../helpers/feature-flag";
import { isDonationForm, isTicketing } from "../helpers/form";
import { getFromLocalStorage } from "../helpers/localStorage";
import { isPaymentMethodAlwaysComplete, listPaymentMethods, ONE_TIME_PAD_MIN } from "../helpers/payment-method";
import { trpc } from "../helpers/trpc";
import { useAmplitude } from "../hooks/amplitude/useAmplitude";
import { useAmplitudeFeatureFlag } from "../hooks/amplitude/useAmplitudeFeatureFlag";
import { useTranslationField } from "../hooks/useDonationTranslation";
import { useFormAmplitudeContext, useLogFormAmplitude } from "../hooks/useFormAmplitudeContext";
import { useIsOnboarding } from "../hooks/useIsOnboarding";
import { useSupportsExpressCheckout } from "../hooks/useSupportsExpressCheckout";

import { getStripeSubscriptionRecurrence } from "@/components/PaymentProcessor/helper";
import { DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "@/types/trpc";

interface FrontendFormProvider {
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  organization: NonNullable<GetCurrentUserFrontendObject["currentOrganization"]>;
  formType: FormType;
  isEmbed: boolean;
  isFundraiser: boolean;
  shouldDisplayLogin: boolean;
  themeColor?: Emptyable<string>;
  loggedOrganization: GetCurrentUserFrontendObject["currentOrganization"] | null | undefined;
}

export const FrontendFormProviderInner: FunctionComponent<React.PropsWithChildren<FrontendFormProvider>> = ({
  children,
  formData,
  organization,
  formType,
  isEmbed,
  isFundraiser,
  shouldDisplayLogin,
  themeColor,
  loggedOrganization,
}) => {
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const firstUpdate = useRef(true);
  const { logAmplitudeEvent } = useAmplitude();
  const { mutate: updatePreviewFirstFormAt } = trpc.updatePreviewFirstFormAt.useMutation();

  const { data: shouldDisplayAddressQuestion } = trpc.form_shouldDisplayAddressQuestion.useQuery({
    formId: formData.id,
    formType,
  });

  const { isOnboarding } = useIsOnboarding();
  const { locale } = useLocaleContext();
  const { amount: defaultAmount } = query as unknown as { amount: string };
  const search = useSearchParams();
  const isPreviewTemplateMode = search.get("previewTemplateMode") === "true" || false;
  const isPreview = search.get(IS_PREVIEW_QUERY_PARAM) === "true" || false;

  const organizationCreatedAtDate = organization.createdAtUtc
    ? convertTimestampToDateNumber(organization.createdAtUtc)
    : "undefined";

  const defaultDonationRecurrence = isDonationForm(formData)
    ? (formData.donationFormFields?.[0]?.donationFormAmounts?.[0]?.recurrenceInterval as
        | ProductDonationRecurrenceInterval
        | undefined) || ProductDonationRecurrenceInterval.OneTime
    : undefined;

  const askForAddress = isDonationForm(formData)
    ? (formData.askForAddress ?? true)
    : isTicketing(formData)
      ? (formData.askForAddress ?? true)
      : false;

  const defaultRecurrence = defaultDonationRecurrence
    ? getStripeSubscriptionRecurrence(defaultDonationRecurrence)
    : null;

  const [stripeRecurrenceInterval, setStripeRecurrenceInterval] = useState<StripeRecurringInterval | null>(
    defaultRecurrence
  );

  const isAuction = isTicketing(formData) && formData?.formCategory === TicketingFormCategory.Auction;
  const paymentMode = isAuction ? "setup" : stripeRecurrenceInterval ? "subscription" : "payment";

  // Calculate peer credibility counts
  const peerCredibilityTotalCount = isTicketing(formData)
    ? formData.formCategory === FormCategory.Event
      ? (formData.totalGuestsCount ?? 0)
      : (formData.totalSupportersCount ?? 0)
    : isDonationForm(formData)
      ? (formData.totalSupportersCount ?? 0)
      : 0;

  const peerCredibilityWeeklyCount = isTicketing(formData)
    ? formData.formCategory === FormCategory.Event
      ? (formData.weeklyGuestsCount ?? 0)
      : (formData.weeklySupportersCount ?? 0)
    : isDonationForm(formData)
      ? (formData.weeklySupportersCount ?? 0)
      : 0;

  const livePeerCredibilityChipVariant = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.LivePeerCredChip,
    userProperties: { organizationId: organization?.id || "", donor_form_id: formData.id || "" },
    organizationId: organization?.id,
    skip: peerCredibilityTotalCount < 50 && peerCredibilityWeeklyCount < 25,
  });

  const {
    expressCheckoutDisabled,
    isExpressCheckoutLoading,
    supportsExpressCheckout,
    showExpressCheckoutLayout,
    handleSetSupportsExpressCheckout,
  } = useSupportsExpressCheckout(paymentMode);

  const defaultSelectedPaymentMethod =
    expressCheckoutDisabled || isPreviewTemplateMode ? PaymentMethod.Card : undefined;

  const commandId = useMemo(() => v4(), []);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPadLoading, setIsPadLoading] = useState(false);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{
    paymentMethod: Undefinable<PaymentMethod>;
    complete: boolean;
  }>({
    paymentMethod: defaultSelectedPaymentMethod,
    complete: false,
  });

  useEffect(() => {
    if (loggedOrganization && !loggedOrganization.previewFirstFormAt && !isPreview && !isPreviewTemplateMode) {
      updatePreviewFirstFormAt();
    }
  }, [isPreview, isPreviewTemplateMode, loggedOrganization, organization.previewFirstFormAt, updatePreviewFirstFormAt]);

  const [displayedFormAmount, setDisplayedFormAmount] = useState<number>(parseInt(defaultAmount) * 100 || 0);
  const [canUsePaymentRequest, setCanUsePaymentRequest] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

  const translationLocales = getTranslationLocales(formData, formType, locale);
  const { field, fieldLength, postTransactionUrl, chequeDescription } = useTranslationField(formData);
  const { formAmplitudeContext } = useFormAmplitudeContext({
    formData,
    field,
    isEmbed,
    organization,
    selectedPaymentMethod: selectedPaymentMethod.paymentMethod,
  });
  const currency = getCurrencyFromCountry(organization?.country);
  const isMembershipV2 = isTicketing(formData) && formData?.formCategory === TicketingFormCategory.MembershipV2;
  const isArchived = isDonationForm(formData) || isTicketing(formData) ? formData.isArchived : false;
  const category = isDonationForm(formData) ? formData.category : formData.formCategory;

  const isFreeTicketing = Boolean(
    isTicketing(formData) && formData.rates?.every((rate) => rate.amount === 0) && !formData.extraDonation
  );

  const {
    tip,
    suggestedTips,
    selectedTip,
    isSuggestedTipsAmount,
    handleChangeTips,
    handleChangeFreeTips,
    resetTipToCoverBankCharge,
    freeTips,
    updateFreeTipAmount,
    fees,
    formattedFees,
    tipExperimentValue,
    getTipChoice,
    logAmplitudeTipsChanged,
    displayTipSelection,
  } = useComputeTip({
    displayedFormAmount,
    currency,
    organizationCountry: organization?.country,
    paymentMethod: selectedPaymentMethod.paymentMethod,
    isAuction,
    category,
    formData,
  });

  /**
   * Set the donor_organization_id and donor_organization_creation_date properties in amplitude.
   */
  useEffect(() => {
    if (organization.id) {
      setUserProperty("donor_organization_id", organization.id);
      setUserProperty("donor_organization_country", organization.country);
      setUserProperty("donor_organization_region", organization.region || "undefined");
      setUserProperty("donor_organization_creation_date", organizationCreatedAtDate);
      setUserProperty("donor_form_id", formData.id || "");
    }
  }, [organizationCreatedAtDate, organization.id, organization.country, organization.region, formData.id]);

  useLogFormAmplitude({ formData, formAmplitudeContext });

  const isPaymentByPadAllowedByFeatureFlag = isDonationForm(formData)
    ? (getDonationFormFeatureFlagTreatment(formData.featureFlagTreatments, FeatureFlagName.IsPaymentByPadAllowed) ??
      false)
    : isTicketing(formData)
      ? true // IsPaymentByPadAllowed is always true for ticketing
      : false;

  const handleSetSelectedPaymentMethod = useCallback(
    (paymentMethod: Undefinable<PaymentMethod>, complete?: boolean) => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
      } else if (paymentMethod !== selectedPaymentMethod.paymentMethod) {
        logAmplitudeEvent(AmplitudeEvents.DonorFormPaymentMethodChanged, {
          paymentMethod,
          formType,
          formId: formData.id,
          organizationCountry: organization.country,
          formCategory: category,
          feesCovered: displayedFormAmount >= ONE_TIME_PAD_MIN && !tip,
        });
      }
      setSelectedPaymentMethod({
        paymentMethod,
        complete: complete ?? isPaymentMethodAlwaysComplete(paymentMethod),
      });
    },
    [
      category,
      displayedFormAmount,
      formData.id,
      formType,
      logAmplitudeEvent,
      organization.country,
      selectedPaymentMethod.paymentMethod,
      tip,
    ]
  );

  const allowedPaymentMethods = useMemo(
    () =>
      listPaymentMethods({
        formData,
        stripeRecurrenceInterval,
        organizationCountry: organization.country,
        totalAmount: displayedFormAmount,
        formType,
        areBankPaymentMethodsAllowed: isPaymentByPadAllowedByFeatureFlag,
        orgCardMaximumAmount: organization.cardMaximumAmount || Infinity,
        isAuction,
        isStripeCustomAccountActive: Boolean(organization.isStripeCustomAccountActive),
        isPreviewTemplateMode,
        isEmbed,
        isFreeTicketing,
      }),
    [
      displayedFormAmount,
      formData,
      formType,
      isAuction,
      isPaymentByPadAllowedByFeatureFlag,
      isPreviewTemplateMode,
      organization.cardMaximumAmount,
      organization.country,
      organization.isStripeCustomAccountActive,
      stripeRecurrenceInterval,
      isEmbed,
      isFreeTicketing,
    ]
  );

  const applePayOrGooglePayAllowed = useMemo(
    () => allowedPaymentMethods.includes(PaymentMethod.ApplePayOrGooglePay),
    [allowedPaymentMethods]
  );

  const achAllowed = useMemo(() => allowedPaymentMethods.includes(PaymentMethod.Ach), [allowedPaymentMethods]);

  const padAllowed = useMemo(() => allowedPaymentMethods.includes(PaymentMethod.Pad), [allowedPaymentMethods]);

  const prioritizeBankPayment = displayedFormAmount >= ONE_TIME_PAD_MIN;

  const expressCheckoutIsSelected =
    supportsExpressCheckout && selectedPaymentMethod.paymentMethod === PaymentMethod.ApplePayOrGooglePay;

  const generateETicket =
    category &&
    [
      TicketingFormCategory.Event,
      TicketingFormCategory.Lottery,
      TicketingFormCategory.Custom,
      TicketingFormCategory.Shop,
    ].includes(category as TicketingFormCategory) &&
    isTicketing(formData) &&
    formData.generateQrCode;

  const displayExpressCheckout =
    applePayOrGooglePayAllowed &&
    !isFreeTicketing &&
    (supportsExpressCheckout || isExpressCheckoutLoading) &&
    !prioritizeBankPayment;

  const showTopBillingInfo = (!isExpressCheckoutLoading && !displayExpressCheckout) || isPreviewTemplateMode;
  const showBottomBillingInfo =
    showExpressCheckoutLayout &&
    Boolean(selectedPaymentMethod.paymentMethod) &&
    selectedPaymentMethod.paymentMethod !== PaymentMethod.ApplePayOrGooglePay &&
    !showTopBillingInfo;

  const isPaymentDisabled = Boolean(organization.isPaymentDisabled);

  return (
    <FrontendFormContext.Provider
      value={{
        tipExperimentValue,
        isFreeTicketing,
        defaultSelectedPaymentMethod,
        formData,
        isArchived,
        field,
        isOnboarding,
        organization,
        commandId,
        formType,
        category,
        themeColor,
        translationLocales,
        isEmbed,
        isFundraiser,
        isFormValid,
        setIsFormValid,
        isFormDirty,
        setIsFormDirty,
        shouldDisplayLanguageSelect: Boolean(fieldLength && fieldLength > 1 && !isOnboarding),
        shouldDisplayLogin: shouldDisplayLogin && !isOnboarding,
        formAmplitudeContext,
        selectedPaymentMethod: selectedPaymentMethod.paymentMethod,
        setSelectedPaymentMethod: handleSetSelectedPaymentMethod,
        currency,
        stripeRecurrenceInterval,
        setStripeRecurrenceInterval,
        tip,
        suggestedTips,
        selectedTip,
        isSuggestedTipsAmount,
        handleChangeTips,
        handleChangeFreeTips,
        resetTipToCoverBankCharge,
        freeTips,
        updateFreeTipAmount,
        fees,
        formattedFees,
        displayTipSelection,
        displayedFormAmount,
        setDisplayedFormAmount,
        displayedFormAmountWithTip: displayedFormAmount + tip,
        isMembershipV2,
        canUsePaymentRequest,
        setCanUsePaymentRequest,
        paymentRequest,
        setPaymentRequest,
        areBankPaymentMethodsAllowed: padAllowed || achAllowed,
        allowedPaymentMethods,
        postTransactionUrl,
        prioritizeBankPayment,
        selectedPaymentMethodIsBank:
          selectedPaymentMethod.paymentMethod === PaymentMethod.Ach ||
          selectedPaymentMethod.paymentMethod === PaymentMethod.Pad,
        selectedPaymentMethodIsCard:
          selectedPaymentMethod.paymentMethod === PaymentMethod.Card ||
          selectedPaymentMethod.paymentMethod === PaymentMethod.ApplePayOrGooglePay,
        chequeDescription,
        showProceedButton:
          selectedPaymentMethod.paymentMethod === PaymentMethod.Pad ||
          (selectedPaymentMethod.paymentMethod === PaymentMethod.Ach && selectedPaymentMethod.complete),
        displayExpressCheckout,
        handleSetSupportsExpressCheckout,
        isExpressCheckoutLoading,
        isSubmitting,
        setIsSubmitting,
        expressCheckoutIsSelected,
        showTopBillingInfo,
        showBottomBillingInfo,
        expressCheckoutDisabled,
        paymentMode,
        isAuction,
        shouldSendTipPercentage: isAuction && !selectedTip.isFree && selectedTip.percentage !== 0,
        getTipChoice,
        logAmplitudeTipsChanged,
        displayAddressQuestion: shouldDisplayAddressQuestion ?? true,
        generateETicket,
        isPreviewTemplateMode,
        isPreview,
        isPaymentDisabled,
        askForAddress,
        livePeerCredibilityChipVariant: livePeerCredibilityChipVariant.isActive,
        peerCredibilityTotalCount,
        peerCredibilityWeeklyCount,
        isPadLoading,
        setIsPadLoading,
      }}
    >
      {children}
    </FrontendFormContext.Provider>
  );
};

const getTranslationLocales = (
  formData: Undefinable<DonationFormFrontendPageOutput | TicketingFrontendPageOutput>,
  formType: Undefinable<FormType>,
  locale: Locales
) => {
  return formType === FormType.DonationForm
    ? Utils.filterAndMap(
        (formData as DonationFormFrontendPageOutput).donationFormFields || [],
        (field) => field.locale !== locale,
        (field) => field.locale
      )
    : formType === FormType.Ticketing
      ? Utils.filterAndMap(
          (formData as TicketingFrontendPageOutput).ticketingFields || [],
          (field) => field.locale !== locale,
          (field) => field.locale
        )
      : [];
};

export const FrontendFormProvider = ({
  children,
  formType,
  isEmbed,
  shouldDisplayLogin,
  isFundraiser,
  themeColor,
  formData,
  organization,
}: {
  children: React.ReactNode;
  formType?: FormType | null;
  isEmbed?: boolean;
  shouldDisplayLogin?: boolean;
  isFundraiser?: boolean;
  themeColor?: Emptyable<string>;
  formData?: DonationFormFrontendPageOutput | TicketingFrontendPageOutput | null;
  organization?: NonNullable<GetCurrentUserFrontendObject["currentOrganization"]> | null;
}) => {
  const isConnectedOnDashboard = getFromLocalStorage?.("isConnectedOnDashboard") === "true";
  const { data: getUserTrpc } = trpc.getFrontendCurrentUser.useQuery(undefined, {
    enabled: Boolean(isConnectedOnDashboard),
  });
  const loggedOrganization = getUserTrpc?.currentOrganization;

  const content = (
    <Stack
      direction="column"
      sx={{
        minHeight: "100vh",
        ["@supports (min-height: 100dvh)"]: {
          minHeight: "100dvh",
        },
        position: "relative",
      }}
    >
      <NotificationBanners form={formData} loggedOrganization={loggedOrganization} />

      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", height: "100%" }}>{children}</Box>
    </Stack>
  );

  return (
    <>
      {organization && formData && formType ? (
        <FrontendFormProviderInner
          formData={formData}
          organization={organization}
          formType={formType}
          isEmbed={isEmbed || false}
          isFundraiser={isFundraiser || false}
          shouldDisplayLogin={shouldDisplayLogin || false}
          themeColor={themeColor || ""}
          loggedOrganization={loggedOrganization}
        >
          {content}
        </FrontendFormProviderInner>
      ) : (
        <>{content}</>
      )}
    </>
  );
};
